import React, { useState } from "react";
import { Box, Notification, RadioCardGroup, Select, Text } from "@telia/teddy";
import { AdditionalSimcard } from "../../../../../../models/state";
import { ExtraSimType } from "../../../../../../models/common";
import { SimConfig, SimExpandSection } from "../RetailerSubscriptionSelectable";
import "./ChooseSimType.less";

type SimType = "VOICE" | Extract<ExtraSimType, "DATASIM" | "TVILLINGSIM">;

interface Props {
    additionalSimcards: AdditionalSimcard[];
    onSelectVoice: () => void;
    onConfirmExtraSim: (params: { simType: ExtraSimType; productCode: string; simConfig: SimConfig }) => void;
    isSubmitting?: boolean;
}

const getSimsForType = (additionalSimcards: AdditionalSimcard[], type: ExtraSimType) =>
    // Watch twin-SIMs (TVILLINGSIM_KLOKKE) are not supported for change yet, so they're excluded here.
    additionalSimcards.filter((sim) => sim.simType === type);

const ChooseSimType: React.FC<Props> = ({ additionalSimcards, onSelectVoice, onConfirmExtraSim, isSubmitting }) => {
    const [type, setType] = useState<SimType | "">("");
    const [selectedSim, setSelectedSim] = useState<AdditionalSimcard | undefined>();

    const handleTypeChange = (value: string) => {
        const nextType = value as SimType;
        setType(nextType);
        setSelectedSim(undefined);

        if (nextType === "VOICE") {
            onSelectVoice();
            return;
        }

        const sims = getSimsForType(additionalSimcards, nextType);
        if (sims.length === 1) {
            setSelectedSim(sims[0]);
        }
    };

    const extraSimType = type === "DATASIM" || type === "TVILLINGSIM" ? type : undefined;
    const sims = extraSimType ? getSimsForType(additionalSimcards, extraSimType) : [];

    return (
        <Box>
            <Text as="p" mb="200">
                <strong>Hva skal erstattes?</strong>
            </Text>
            <RadioCardGroup value={type} onValueChange={handleTypeChange} orientation="vertical" name="choose-sim-type">
                <RadioCardGroup.Item value="VOICE">
                    <RadioCardGroup.Title>VOICE-SIM (hoved-SIM)</RadioCardGroup.Title>
                </RadioCardGroup.Item>
                <RadioCardGroup.Item
                    value="DATASIM"
                    disabled={getSimsForType(additionalSimcards, "DATASIM").length === 0}
                >
                    <RadioCardGroup.Title>Data-SIM</RadioCardGroup.Title>
                </RadioCardGroup.Item>
                <RadioCardGroup.Item
                    value="TVILLINGSIM"
                    disabled={getSimsForType(additionalSimcards, "TVILLINGSIM").length === 0}
                >
                    <RadioCardGroup.Title>Tvilling-SIM</RadioCardGroup.Title>
                </RadioCardGroup.Item>
            </RadioCardGroup>

            {extraSimType && sims.length > 1 && (
                <Box mt="200">
                    <Select
                        className="choose-sim-type__select"
                        name="choose-sim-type-sim-select"
                        label="Velg SIM-kort som skal byttes"
                        options={sims.map((sim) => sim.name)}
                        value={selectedSim?.name || ""}
                        onValueChange={(value) => {
                            setSelectedSim(sims.find((sim) => sim.name === value));
                            return value;
                        }}
                    >
                        <Select.Trigger>
                            <Select.Value placeholder="Velg SIM-kort" />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Viewport>
                                {sims.map((sim) => (
                                    <Select.Item key={sim.name} value={sim.name}>
                                        {sim.simCardType} - {sim.simNumber}
                                    </Select.Item>
                                ))}
                            </Select.Viewport>
                        </Select.Content>
                    </Select>
                </Box>
            )}

            {extraSimType && selectedSim && (
                <Box mt="200">
                    {selectedSim.productCode ? (
                        <SimExpandSection
                            isSubmitting={isSubmitting}
                            onConfirm={(simConfig: SimConfig) =>
                                onConfirmExtraSim({
                                    simType: extraSimType,
                                    productCode: selectedSim.productCode as string,
                                    simConfig,
                                })
                            }
                        />
                    ) : (
                        <Notification type="tile" variant="error">
                            <Notification.Icon />
                            <Notification.Text>
                                Kan ikke bytte dette SIM-kortet akkurat nå. Prøv igjen senere.
                            </Notification.Text>
                        </Notification>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ChooseSimType;
